/**
 * Patient360SmartSection.tsx — Collapsible smart section card
 * with priority badges and persistent collapse state.
 */

import { useState, useEffect, memo } from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useDensityContext } from '../../contexts/DensityContext';
import type { DynamicEntityResolvedField } from '../../types/dynamicEntityView';
import { DynamicFieldDisplay } from '../dynamic-view/DynamicFieldDisplay';

// ─── Types ──────────────────────────────────────────────────

export interface SmartSectionProps {
  sectionKey: string;
  title: string;
  fields: DynamicEntityResolvedField[];
  badges?: string[];
  collapsible?: boolean;
  defaultExpanded?: boolean;
  isEmpty?: boolean;
  priorityScore?: number;
  programId?: string;
  patientId?: string;
  columns?: 1 | 2 | 3;
  className?: string;
  children?: React.ReactNode;
}

// ─── Persistence helpers ─────────────────────────────────────

function getCollapseKey(programId?: string, patientId?: string, sectionKey?: string) {
  return `patient360:${programId ?? ''}:${patientId ?? ''}:${sectionKey ?? ''}`;
}

function getPersistedExpanded(key: string, defaultExpanded: boolean): boolean {
  try {
    const stored = localStorage.getItem(key);
    if (stored === 'true') return true;
    if (stored === 'false') return false;
  } catch { /* ignore */ }
  return defaultExpanded;
}

function persistExpanded(key: string, expanded: boolean) {
  try {
    localStorage.setItem(key, String(expanded));
  } catch { /* ignore */ }
}

// ─── Badge variants ─────────────────────────────────────────

const BADGE_VARIANTS: Record<string, string> = {
  'Urgent': 'bg-destructive/10 text-destructive border-destructive/20',
  'Low Balance': 'bg-warning-soft text-warning-strong border-warning/20',
  'Expiring Soon': 'bg-warning-soft text-warning-strong border-warning/20',
  'Renewal Due': 'bg-warning-soft text-warning-strong border-warning/20',
  'Missing Docs': 'bg-destructive/10 text-destructive border-destructive/20',
  'Active Cycle': 'bg-success-soft text-success border-success/20',
  'Recent Activity': 'bg-primary-soft text-primary border-primary/20',
  'Workflow Required': 'bg-primary-soft text-primary border-primary/20',
  'No Active Enrollment': 'bg-slate-100/70 text-slate-600 border-slate-200 dark:bg-slate-800/50 dark:text-slate-400 dark:border-slate-700',
};

function getBadgeClass(badge: string): string {
  return BADGE_VARIANTS[badge] ?? 'bg-info-soft text-info border-info/20';
}

// ─── Component ───────────────────────────────────────────────

export const Patient360SmartSection = memo(function Patient360SmartSection({
  sectionKey,
  title,
  fields,
  badges = [],
  collapsible = true,
  defaultExpanded = true,
  isEmpty = false,
  programId,
  patientId,
  columns = 2,
  className,
  children,
}: SmartSectionProps) {
  const { isCompact } = useDensityContext();

  const persistKey = getCollapseKey(programId, patientId, sectionKey);
  const [expanded, setExpanded] = useState(() =>
    getPersistedExpanded(persistKey, defaultExpanded),
  );

  // Sync with defaultExpanded when it changes (e.g. priority re-scoring)
  useEffect(() => {
    const stored = localStorage.getItem(persistKey);
    // Only auto-expand if user hasn't manually collapsed
    if (stored === null && defaultExpanded !== expanded) {
      setExpanded(defaultExpanded);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultExpanded, persistKey]);

  const handleToggle = () => {
    if (!collapsible) return;
    const next = !expanded;
    setExpanded(next);
    persistExpanded(persistKey, next);
  };

  // Don't render empty sections with no badges and no children
  if (isEmpty && badges.length === 0 && !children) return null;

  const sortedFields = fields
    .filter(f => f.visible)
    .sort((a, b) => a.order - b.order);

  const gridCols =
    columns === 3
      ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'
      : columns === 2
        ? 'grid-cols-1 md:grid-cols-2'
        : 'grid-cols-1';

  return (
    <div
      className={cn(
        'rounded-xl border border-border-hairline bg-surface-premium',
        className,
      )}
      data-section-key={sectionKey}
    >
      {/* Header */}
      <button
        type="button"
        onClick={handleToggle}
        disabled={!collapsible}
        className={cn(
          'w-full flex items-center gap-2 text-left',
          isCompact ? 'px-3 py-2' : 'px-4 py-3',
          collapsible && 'cursor-pointer hover:bg-muted/30 transition-colors',
          !collapsible && 'cursor-default',
        )}
        aria-expanded={expanded}
      >
        <span className="text-[12px] font-semibold text-foreground flex-1 min-w-0 truncate">
          {title}
        </span>

        {/* Badges */}
        {badges.length > 0 && (
          <div className="flex items-center gap-1 flex-shrink-0">
            {badges.map(badge => (
              <span
                key={badge}
                className={cn(
                  'inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-medium border',
                  getBadgeClass(badge),
                )}
              >
                {badge}
              </span>
            ))}
          </div>
        )}

        {/* Collapse chevron */}
        {collapsible && (
          <ChevronDown
            className={cn(
              'w-3.5 h-3.5 text-muted-foreground transition-transform duration-200 flex-shrink-0',
              expanded && 'rotate-180',
            )}
            aria-hidden="true"
          />
        )}
      </button>

      {/* Body */}
      {expanded && (
        <div className={cn('pt-1', isCompact ? 'px-3 pb-3' : 'px-4 pb-4')}>
          {children ? (
            children
          ) : sortedFields.length > 0 ? (
            <dl className={cn('grid gap-3', gridCols)}>
              {sortedFields.map(field => (
                <DynamicFieldDisplay key={field.fieldKey} field={field} />
              ))}
            </dl>
          ) : isEmpty ? (
            <div className="rounded-lg bg-muted/30 p-3">
              <p className="text-[11px] text-muted-foreground">No data available</p>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
});
